import { getPageSettings } from "../util/pageSettings";
import { getBrandJSON } from "../util/locationBasedBrandJson";
import { cachedAPIWrapper } from "../apiCaching/apiWrapper";
import { REGEX } from "../constants/regex";

const conceptIdSet: Set<string> = new Set([
    process.env.CONCEPT_NBLY, process.env.CONCEPT_CORP
]
.filter((v): v is string => !!v && v.trim().length > 0)
.map(v => v.trim().toLowerCase())
);

export function conceptIdLookupForNblyOrCorp(): boolean {
    const el = document.getElementById('conceptId') as HTMLInputElement | null;
    const value = el?.value ? el.value.trim().toLowerCase() : '';
    return !!value && conceptIdSet.has(value);
}

export function conceptIdForAllBrands(): string | null {
    
    const isNblyOrCorp = conceptIdLookupForNblyOrCorp();
    if(isNblyOrCorp) {
        const alternateConceptId = getPageSettings('metaInfo')?.AlternateConceptId;
        if(alternateConceptId) {
            return alternateConceptId;
        }
        return null;
    }
    const conceptId: any = document.getElementById('conceptId');
    return conceptId?.value || null;
}

export function returnBrandUrl(): string | null {
    const isNblyOrCorp = conceptIdLookupForNblyOrCorp();
    if(isNblyOrCorp) {
        const pageDomain = getPageSettings('metaInfo')?.PageDomain;
        if(pageDomain) {
            return pageDomain;
        }
    }
    return null;
}

/**
 * Returns the brand URL for the given brand code.
 * @param brandCode The brand code.
 * @returns The brand URL.
 */

interface SecondaryDomain {
    domainName: string;
    landingPage: string;
}

interface BrandJson {
    code: string;
    name: string;
    url: string;
    vendorName: string;
    secondary_domains: SecondaryDomain[];
}

type NormalizedBrand = {
    code: string;
    name: string;
    url: string;
    "www-url": string;
    vendorName: string;
    imageUrl: string;
    redirectUrl: string;
    [key: `secondaryDomainName${number}`]: string;
};

/**
 * Constructs normalized brand objects.
 * Skips brands whose images 404.
 */

export async function constructNblyObject(): Promise<NormalizedBrand[]> {
    const allBrandJson = await getAllBrandJson();

    const origin =
        typeof window !== "undefined" ? window.location.origin : "";

    const brandPromises = allBrandJson.map(async (brandJson) => {
        const { url, "www-url": wwwUrl } = buildUrlVariants(brandJson.url);
        if(!url || !wwwUrl) return null;

        const imageUrl = `${origin}/brand/_assets/images/multiple-brand-logo/${normalizeBandData(
        brandJson.code
        )}-brand-logo.svg`;

        // Skip brands whose images don't exist
        const exists = await cachedImageExists(imageUrl);
        if (!exists) return null;

        const result: NormalizedBrand = {
        code: normalizeBandData(brandJson.code),
        name: normalizeBandData(brandJson.name),
        url,
        "www-url": wwwUrl,
        vendorName: normalizeBandData(brandJson.vendorName),
        imageUrl,
        redirectUrl: brandJson.url,
        };

        brandJson.secondary_domains.forEach((domain, index) => {
        result[`secondaryDomainName${index}`] =
            normalizeBandData(domain.domainName);
        });

        return result;
    });

    const resolved = await Promise.all(brandPromises);
    return resolved.filter((brand): brand is NormalizedBrand => brand !== null);
}

/**
 * In-memory cache for image existence
 */
const imageCache = new Map<string, boolean>();

/**
 * Cached version of imageExists
 */
async function cachedImageExists(url: string): Promise<boolean> {
    if (imageCache.has(url)) return imageCache.get(url)!;

    const exists = await imageExists(url);
    imageCache.set(url, exists);
    return exists;
}

/**
 * Checks if an image exists (non-404)
 */
async function imageExists(url: string): Promise<boolean> {
    try {
        const res = await fetch(url, { method: "HEAD" });
        return res.ok;
    } catch {
        return false;
    }
}

/**
 * Builds url variants: url (no www) and www-url
 */
function buildUrlVariants(
  rawUrl?: string
): { url: string; "www-url": string } {
    const cleaned = normalizeBandData(rawUrl ?? "");

    const protocolMatch = /^(https?)/.exec(cleaned);

    if (!protocolMatch) {
        // No protocol → just prefix www
        return {
        url: cleaned,
        "www-url": `www${cleaned}`,
        };
    }

    const protocol = protocolMatch[0];
    const rest = cleaned.slice(protocol.length);

    return {
        url: cleaned.replace(/^https?www/, protocol),
        "www-url": `${protocol}www${rest.replace(/^www/, "")}`,
    };
}

/**
 * Fetches all brand JSON data
 */
async function getAllBrandJson(): Promise<BrandJson[]> {
  try {
    const brandJsonPath = getBrandJSON();

    if (brandJsonPath === "Error validating Location") {
      console.error("Error determining BrandJSON path for given location");
      return [];
    }

    const origin =
      typeof window !== "undefined" ? window.location.origin : "";

    const res = await cachedAPIWrapper(`${origin}/${brandJsonPath}`);
    return Array.isArray(res) ? res : [];
  } catch (error) {
    console.error("Error fetching BrandJSON:", error);
    return [];
  }
}

/**
 * Normalizes brand string: remove special chars + lowercase
 */
function normalizeBandData(value?: string): string {
    return removeSpecialCharacters(value ?? "").toLowerCase();
}

export function removeSpecialCharacters(input: string): string {
    return input.replace(/[^a-zA-Z0-9]/g, "");
}

export function isValidCAPostalCode(postalCode: string): boolean {
    if (!postalCode) return false;
    
    const trimmed = postalCode.trim().toUpperCase();
    
    return REGEX.caPostalCodeFSA.test(trimmed) || REGEX.caPostalCodeFull.test(trimmed);
}

export function formatCAPostalCode(postalCode: string): string {
    const trimmed = postalCode?.trim().toUpperCase() ?? '';
    
    if (isValidCAPostalCode(trimmed) && trimmed.length === 6) {
        return `${trimmed.substring(0, 3)} ${trimmed.substring(3)}`;
    }

    return trimmed;
}

export function isValidUSZipCode(zipCode: string): boolean {
    if (!zipCode) return false;
    
    const trimmed = zipCode.trim();
    
    return REGEX.usZipCode.test(trimmed);
}

export function validateLocalZipPostalCode(postal: any): boolean {
    const urlParts = window.location.href.toLowerCase().split('/');
    const countryCode = urlParts[3] === "ca" ? "ca" : "us";

    if (typeof postal !== "string") {
        postal = postal.toString();
    }

    const trimmedPostalCode = postal.trim();

    if (countryCode === 'ca') {
        return isValidCAPostalCode(trimmedPostalCode);
    }

    if (countryCode === 'us') {
        return isValidUSZipCode(trimmedPostalCode) || isValidCAPostalCode(trimmedPostalCode);
    }

    return false;
}

// ----------------------------------------------
export function getBrandFilter(searchTerm: string, zipCode: string): string[] {
    const normalizedSearchTerm = normalizeBandData(searchTerm).toLowerCase();
    const savedBrands = localStorage.getItem('nblyBrands');
    if (!savedBrands) return [];
    
    try {
        const brands: NormalizedBrand[] = JSON.parse(savedBrands);
        
        const matchedBrands = brands.filter((brand: NormalizedBrand) => {
            return Object.values(brand).some((value) => {
                if (typeof value === 'string') {
                    return value.toLowerCase().includes(normalizedSearchTerm);
                }
                return false;
            });
        });
        const deduplicatedBrands = deduplicateMhmBrands(matchedBrands);
        const sortedBrands: NormalizedBrand[] = deduplicatedBrands.sort((a, b) => a.name.localeCompare(b.name));
        let brandTemplate: string[] = [];
        if(sortedBrands.length > 0) {
            sortedBrands.forEach((brand: NormalizedBrand) => {
            brandTemplate.push(checkAndReturnCARedirects(brand, zipCode));
            });
        }
        return brandTemplate;
    } catch (error) {
        console.error('Error parsing nblyBrands from localStorage:', error);
        return [];
    }
}
function isLowerEnvironment(url: string): boolean {
    return url.includes('nblysbx') || url.includes('nblydev') || url.includes('nblytest');
}
function deduplicateMhmBrands(brands: NormalizedBrand[]): NormalizedBrand[] {
    const hasMrHandyman = brands.some((brand: NormalizedBrand) => 
        brand.code?.toLowerCase() === 'mhm' && brand.name?.toLowerCase() === 'mrhandyman'
    );
    return hasMrHandyman 
        ? brands.filter((brand: NormalizedBrand) => brand.name?.toLowerCase() !== 'myhandyman')
        : brands;
}
function checkAndReturnCARedirects(brandDetails:NormalizedBrand, zipCode: string){
    let brandRedirect = brandDetails?.redirectUrl;
    if(isValidCAPostalCode(zipCode)){
        const caDomainBrands = ['mrr', 'mra', 'juk', 'mrh', 'mhm', 'asv', 'pds', 'guy'];
        if(caDomainBrands.includes(brandDetails?.code?.toLowerCase() ?? '') && brandDetails?.name !== 'myhandyman'){
             if(isLowerEnvironment(brandRedirect)){
                brandRedirect = brandRedirect.replace('-stage', '-ca-stage');
                brandRedirect = brandRedirect.replace('-test', '-ca-test');
                brandRedirect = brandRedirect.replace('-dev', '-ca-dev');
            }else{
                brandRedirect = brandRedirect.replace('.nblyprod', '-ca.nblyprod');
                brandRedirect = brandRedirect.replace('.com', '.ca');
            }
        }
    }
    return `<li class="brand-item">
                                <a href="${brandRedirect}" class="brand-link" target="_blank">
                                    <img src="${brandDetails.imageUrl}" alt="${brandDetails.name}" class="brand-logo" width="auto" height="50">
                                    <svg aria-hidden="true" class="icon">
                                        <use xlink:href="/_assets-global/images/hero-icon-sprite.svg#arrow-up-right"></use>
                                    </svg>
                                </a>
                            </li>`;
}